<script>
import { mapGetters, mapActions } from 'vuex';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import validations from './validations';
import SlaTimeInput from './SlaTimeInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useVuelidate } from '@vuelidate/core';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';

export default {
  components: {
    SlaTimeInput,
    NextButton,
    ToggleSwitch,
    ComboBoxDropdown,
  },
  props: {
    selectedResponse: {
      type: Object,
      default: () => {},
    },
    submitLabel: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'submitSla'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      name: '',
      description: '',
      isSlaTimeInputsInvalid: false,
      slaTimeInputsValidation: {},
      slaTimeInputs: [
        {
          threshold: null,
          unit: 'Minutes',
        },
        {
          threshold: null,
          unit: 'Minutes',
        },
        {
          threshold: null,
          unit: 'Minutes',
        },
      ],
      onlyDuringBusinessHours: false,
      excludeLabeledConversations: false,
      excludedLabelIds: [],
      excludeContactReasons: false,
      excludedContactReasonIds: [],
      excludeCommentCreatedConversations: false,
      labelSearchValue: '',
      contactReasonSearchValue: '',
      showLabelDropdown: false,
      showContactReasonDropdown: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'sla/getUIFlags',
      labels: 'labels/getLabels',
      contactReasons: 'contactReasons/getContactReasons',
    }),
    isSubmitDisabled() {
      return (
        this.v$.name.$invalid ||
        this.isSlaTimeInputsInvalid ||
        this.uiFlags.isUpdating
      );
    },
    slaNameErrorMessage() {
      let errorMessage = '';
      if (this.v$.name.$error) {
        if (!this.v$.name.required) {
          errorMessage = this.$t('SLA.FORM.NAME.REQUIRED_ERROR');
        } else if (!this.v$.name.minLength) {
          errorMessage = this.$t('SLA.FORM.NAME.MINIMUM_LENGTH_ERROR');
        }
      }
      return errorMessage;
    },
    filteredLabels() {
      return this.labels
        .filter(label =>
          label.title
            .toLowerCase()
            .includes(this.labelSearchValue.toLowerCase())
        )
        .map(label => ({
          value: label.id,
          label: label.title,
        }));
    },
    filteredContactReasons() {
      const search = this.contactReasonSearchValue.toLowerCase();
      return this.flattenContactReasonTree(this.contactReasons).filter(
        item => !search || item.name.toLowerCase().includes(search)
      );
    },
  },
  mounted() {
    this.fetchContactReasons();
    if (this.selectedResponse) this.setFormValues();
  },
  methods: {
    ...mapActions({ fetchContactReasons: 'contactReasons/get' }),
    flattenContactReasonTree(reasons, level = 0) {
      return reasons.flatMap(reason => {
        const indent = '— '.repeat(level);
        const entry = {
          value: reason.id,
          label: indent + reason.name,
          name: reason.name,
        };
        const children = reason.child_reasons || [];
        return [entry, ...this.flattenContactReasonTree(children, level + 1)];
      });
    },
    onClose() {
      this.$emit('close');
    },
    setFormValues() {
      const {
        name,
        description,
        first_response_time_threshold: firstResponseTimeThreshold,
        next_response_time_threshold: nextResponseTimeThreshold,
        resolution_time_threshold: resolutionTimeThreshold,
        only_during_business_hours: onlyDuringBusinessHours,
        exclude_labeled_conversations: excludeLabeledConversations,
        excluded_label_ids: excludedLabelIds,
        exclude_contact_reasons: excludeContactReasons,
        excluded_contact_reason_ids: excludedContactReasonIds,
        exclude_comment_created_conversations:
          excludeCommentCreatedConversations,
      } = this.selectedResponse;

      this.name = name;
      this.description = description;
      this.onlyDuringBusinessHours = onlyDuringBusinessHours;
      this.excludeLabeledConversations = !!excludeLabeledConversations;
      this.excludeContactReasons = !!excludeContactReasons;
      this.excludeCommentCreatedConversations =
        !!excludeCommentCreatedConversations;

      if (excludedLabelIds && excludedLabelIds.length) {
        this.excludedLabelIds = excludedLabelIds;
      }

      // Map excluded contact reason IDs back to contact reason objects for the multiselect
      if (excludedContactReasonIds && excludedContactReasonIds.length) {
        this.excludedContactReasonIds = excludedContactReasonIds;
      }

      const thresholds = [
        firstResponseTimeThreshold,
        nextResponseTimeThreshold,
        resolutionTimeThreshold,
      ];
      this.slaTimeInputs.forEach((input, index) => {
        const converted = convertSecondsToTimeUnit(thresholds[index], {
          minute: 'Minutes',
          hour: 'Hours',
          day: 'Days',
        });
        input.threshold = converted.time;
        input.unit = converted.unit;
      });
    },
    updateThreshold(index, value) {
      this.slaTimeInputs[index].threshold = value;
    },
    updateUnit(index, unit) {
      this.slaTimeInputs[index].unit = unit;
    },
    getSlaTimeInputLabel(index) {
      if (index === 0) return this.$t('SLA.FORM.FIRST_RESPONSE_TIME.LABEL');
      if (index === 1) return this.$t('SLA.FORM.NEXT_RESPONSE_TIME.LABEL');
      return this.$t('SLA.FORM.RESOLUTION_TIME.LABEL');
    },
    getSlaTimeInputPlaceholder(index) {
      if (index === 0) {
        return this.$t('SLA.FORM.FIRST_RESPONSE_TIME.PLACEHOLDER');
      }
      if (index === 1) {
        return this.$t('SLA.FORM.NEXT_RESPONSE_TIME.PLACEHOLDER');
      }
      return this.$t('SLA.FORM.RESOLUTION_TIME.PLACEHOLDER');
    },
    onSubmit() {
      const payload = {
        name: this.name,
        description: this.description,
        first_response_time_threshold: this.convertToSeconds(0),
        next_response_time_threshold: this.convertToSeconds(1),
        resolution_time_threshold: this.convertToSeconds(2),
        only_during_business_hours: this.onlyDuringBusinessHours,
        exclude_labeled_conversations: this.excludeLabeledConversations,
        excluded_label_ids: this.excludedLabelIds,
        exclude_contact_reasons: this.excludeContactReasons,
        excluded_contact_reason_ids: this.excludedContactReasonIds,
        exclude_comment_created_conversations:
          this.excludeCommentCreatedConversations,
      };
      this.$emit('submitSla', payload);
    },
    convertToSeconds(index) {
      const { threshold, unit } = this.slaTimeInputs[index];
      if (threshold === null || threshold === 0) return null;
      const unitsToSeconds = { Minutes: 60, Hours: 3600, Days: 86400 };
      return Number(threshold * (unitsToSeconds[unit] || 1));
    },
    handleIsInvalid(index, isInvalid) {
      this.slaTimeInputsValidation = {
        ...this.slaTimeInputsValidation,
        [index]: isInvalid,
      };

      this.checkValidationState();
    },
    checkValidationState() {
      const isAnyInvalid = Object.values(this.slaTimeInputsValidation).some(
        isInvalid => isInvalid
      );
      this.isSlaTimeInputsInvalid = isAnyInvalid;
    },
    toggleLabelSelection(item) {
      if (this.excludedLabelIds.includes(item.value)) {
        this.excludedLabelIds = this.excludedLabelIds.filter(
          id => id !== item.value
        );
      } else {
        this.excludedLabelIds.push(item.value);
      }
    },
    toggleContactReasonSelection(item) {
      if (this.excludedContactReasonIds.includes(item.value)) {
        this.excludedContactReasonIds = this.excludedContactReasonIds.filter(
          id => id !== item.value
        );
      } else {
        this.excludedContactReasonIds.push(item.value);
      }
    },
    onLabelSearch(value) {
      this.labelSearchValue = value;
    },
    onContactReasonSearch(value) {
      this.contactReasonSearchValue = value;
    },
    onClickAwayLabelDropdown() {
      this.showLabelDropdown = false;
    },
    onClickAwayContactReasonDropdown() {
      this.showContactReasonDropdown = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <form class="flex flex-wrap mx-0" @submit.prevent="onSubmit">
      <woot-input
        v-model="name"
        :class="{ error: v$.name.$error }"
        class="w-full"
        :styles="{
          borderRadius: '0.75rem',
          padding: '0.375rem 0.75rem',
          fontSize: '0.875rem',
        }"
        :label="$t('SLA.FORM.NAME.LABEL')"
        :placeholder="$t('SLA.FORM.NAME.PLACEHOLDER')"
        :error="slaNameErrorMessage"
        @input="v$.name.$touch"
        @blur="v$.name.$touch"
      />
      <woot-input
        v-model="description"
        class="w-full"
        :styles="{
          borderRadius: '0.75rem',
          padding: '0.375rem 0.75rem',
          fontSize: '0.875rem',
        }"
        :label="$t('SLA.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('SLA.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <SlaTimeInput
        v-for="(input, index) in slaTimeInputs"
        :key="index"
        :threshold="input.threshold"
        :threshold-unit="input.unit"
        :label="getSlaTimeInputLabel(index)"
        :placeholder="getSlaTimeInputPlaceholder(index)"
        @update-threshold="updateThreshold(index, $event)"
        @unit="updateUnit(index, $event)"
        @is-in-valid="handleIsInvalid(index, $event)"
      />

      <div
        class="mt-3 flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-xl justify-between"
      >
        <span for="sla_bh" class="text-n-slate-11">
          {{ $t('SLA.FORM.BUSINESS_HOURS.PLACEHOLDER') }}
        </span>
        <ToggleSwitch id="sla_bh" v-model="onlyDuringBusinessHours" />
      </div>

      <!-- Exclusion Filters -->
      <div class="w-full mt-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('SLA.FORM.FILTERS.SECTION_TITLE') }}
        </p>

        <!-- Exclude labeled conversations -->
        <div
          class="flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-xl justify-between mb-2"
        >
          <span class="text-n-slate-11">
            {{ $t('SLA.FORM.FILTERS.EXCLUDE_LABELED.LABEL') }}
          </span>
          <ToggleSwitch
            id="sla_exclude_labeled"
            v-model="excludeLabeledConversations"
          />
        </div>

        <!-- Label multiselect (shown only when exclude_labeled is enabled) -->
        <div v-if="excludeLabeledConversations" class="w-full mb-2">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ $t('SLA.FORM.FILTERS.EXCLUDED_LABEL_IDS.LABEL') }}
          </label>
          <div
            v-on-clickaway="onClickAwayLabelDropdown"
            class="relative"
            @click="showLabelDropdown = true"
          >
            <div
              class="flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md cursor-pointer border-n-strong bg-n-alpha-1 text-n-slate-12"
            >
              <span
                v-if="excludedLabelIds.length === 0"
                class="text-n-slate-11"
              >
                {{ $t('SLA.FORM.FILTERS.EXCLUDED_LABEL_IDS.PLACEHOLDER') }}
              </span>
              <span v-else>
                {{
                  $t('SLA.FORM.FILTERS.SELECTED_COUNT', {
                    count: excludedLabelIds.length,
                  })
                }}
              </span>
              <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
            </div>
            <ComboBoxDropdown
              :open="showLabelDropdown"
              :options="filteredLabels"
              :selected-values="excludedLabelIds"
              :search-value="labelSearchValue"
              :search-placeholder="$t('SLA.FORM.FILTERS.SEARCH_LABELS')"
              multiple
              @select="toggleLabelSelection"
              @search="onLabelSearch"
            />
          </div>
        </div>

        <!-- Exclude contact reason conversations -->
        <div
          class="flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-xl justify-between mb-2"
        >
          <span class="text-n-slate-11">
            {{ $t('SLA.FORM.FILTERS.EXCLUDE_CONTACT_REASONS.LABEL') }}
          </span>
          <ToggleSwitch
            id="sla_exclude_contact_reasons"
            v-model="excludeContactReasons"
          />
        </div>

        <!-- Contact reason multiselect (shown only when exclude_contact_reasons is enabled) -->
        <div v-if="excludeContactReasons" class="w-full mb-2">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ $t('SLA.FORM.FILTERS.EXCLUDED_CONTACT_REASON_IDS.LABEL') }}
          </label>
          <div
            v-on-clickaway="onClickAwayContactReasonDropdown"
            class="relative"
            @click="showContactReasonDropdown = true"
          >
            <div
              class="flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md cursor-pointer border-n-strong bg-n-alpha-1 text-n-slate-12"
            >
              <span
                v-if="excludedContactReasonIds.length === 0"
                class="text-n-slate-11"
              >
                {{
                  $t('SLA.FORM.FILTERS.EXCLUDED_CONTACT_REASON_IDS.PLACEHOLDER')
                }}
              </span>
              <span v-else>
                {{
                  $t('SLA.FORM.FILTERS.SELECTED_COUNT', {
                    count: excludedContactReasonIds.length,
                  })
                }}
              </span>
              <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
            </div>
            <ComboBoxDropdown
              :open="showContactReasonDropdown"
              :options="filteredContactReasons"
              :selected-values="excludedContactReasonIds"
              :search-value="contactReasonSearchValue"
              :search-placeholder="
                $t('SLA.FORM.FILTERS.SEARCH_CONTACT_REASONS')
              "
              multiple
              @select="toggleContactReasonSelection"
              @search="onContactReasonSearch"
            />
          </div>
        </div>

        <!-- Exclude comment-created conversations -->
        <div
          class="flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-xl justify-between"
        >
          <span class="text-n-slate-11">
            {{ $t('SLA.FORM.FILTERS.EXCLUDE_COMMENT_CREATED.LABEL') }}
          </span>
          <ToggleSwitch
            id="sla_exclude_comment"
            v-model="excludeCommentCreatedConversations"
          />
        </div>
      </div>

      <div class="flex items-center justify-end w-full gap-2 mt-8">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('SLA.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="submitLabel"
          :disabled="isSubmitDisabled"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
